import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchSort } from "@storyboard/dispatch-view/view";
import type { DispatchTableRow } from "./dispatchTableRow";

function scalar(value: string | string[] | undefined): string {
  if (value === undefined) return "";
  return Array.isArray(value) ? value.join(", ") : value;
}

/**
 * The comparator for a field's values, by its kind: numbers by value, booleans with yes above no, timestamps by
 * instant, everything else as case-insensitive text with digit runs ordered by value so aliases like `980`, `1042`,
 * and `T-100` land in one consistent order. Ascending; the caller flips the sign for descending.
 */
export function compareByKind(kind: FieldKind): (a: string, b: string) => number {
  switch (kind.valueType) {
    case "number":
      return (a, b) => Number(a) - Number(b);
    case "boolean":
      // "true" ranks above "false", so a descending sort on the acknowledgement puts the waiting rows first.
      return (a, b) => (a === b ? 0 : a === "true" ? 1 : -1);
    case "timestamp":
      return (a, b) => Date.parse(a) - Date.parse(b);
    default:
      return (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  }
}

/**
 * The direction a freshly chosen sort field starts in: newest or largest first for times and counts, yes first for a
 * boolean (the waiting rows on top when the sort is the acknowledgement), A to Z for everything else.
 */
export function defaultSortDirection(kind: FieldKind): DispatchSort["direction"] {
  return kind.valueType === "timestamp" || kind.valueType === "number" || kind.valueType === "boolean" ? "desc" : "asc";
}

/**
 * Orders rows by the sort applied to their projected values, ties broken by membership id so the order is stable
 * across recomputes.
 *
 * The comparator follows the field's kind ({@link compareByKind}), chosen once per column so it stays transitive.
 * Blanks sort after every non-empty value in either direction, so they never lead a "newest first" list.
 *
 * @param rows - The rows to order.
 * @param sort - The field and direction.
 * @param kind - The sort field's kind, which decides how two values compare.
 */
export function sortDispatchRows(
  rows: readonly DispatchTableRow[],
  sort: DispatchSort,
  kind: FieldKind,
): DispatchTableRow[] {
  const key = sort.field;
  const sign = sort.direction === "asc" ? 1 : -1;
  const compare = compareByKind(kind);
  return rows
    .map((row) => ({ row, value: scalar(row.record[key]) }))
    .sort((a, b) => {
      if (a.value === "" || b.value === "") return a.value === b.value ? 0 : a.value === "" ? 1 : -1;
      const byValue = compare(a.value, b.value) * sign;
      return byValue !== 0
        ? byValue
        : a.row.driver.workspaceMembershipId.localeCompare(b.row.driver.workspaceMembershipId);
    })
    .map(({ row }) => row);
}
